<template>
  <v-container>
    <v-card class="pa-5" outlined>
      <v-card-title>
        <v-icon large class="mr-3">mdi-account-circle</v-icon>
        <span class="text-h5">Edit Profile</span>
      </v-card-title>
      <v-card-subtitle>
        Update your profile information and avatar.
      </v-card-subtitle>
      <v-card-text>
        <v-form ref="form" v-model="isValid" lazy-validation>
          <v-row >
            <!-- Avatar with Hover Effect -->
            <v-col cols="12" md="4" class="d-flex flex-column align-items-center">
              <div class="avatar-container">
                <v-avatar size="128" class="mb-4">
                  <img
                    :src="formData.image || defaultAvatar"
                    alt="Avatar"
                    class="circle-image"
                  />
                </v-avatar>
                <div class="hover-overlay"  @click="triggerImageUpload">
                  <v-icon large>mdi-camera</v-icon>
                </div>
              </div>
              <v-file-input
                ref="imageInput"
                accept="image/*"
                v-model="formData.imageFile"
                class="d-none"
              ></v-file-input>

              <!-- Roles Display -->
              <v-row class="mt-3 no-flex" justify="center">
                <v-chip
                  v-for="role in formData.roles"
                  :key="role"
                  class="me-2"
                  :color="getRoleColor(role)"
                  dark
                  small
                >
                  <v-icon start>{{ getRoleIcon(role) }}</v-icon>
                  {{ getRoleText(role) }}
                </v-chip>
              </v-row>
            </v-col>

            <!-- User Info Fields -->
            <v-col cols="12" md="8">
              <v-text-field
                v-model="formData.username"
                label="Username"
                outlined
                :rules="[v => !!v || 'Username is required']"
                required
              ></v-text-field>
              <v-text-field
                v-model="formData.email"
                label="Email"
                outlined
                :rules="[v => /.+@.+\..+/.test(v) || 'E-mail must be valid']"
                required
              ></v-text-field>

              <!-- Password Fields -->
              <v-text-field
                v-model="formData.oldPassword"
                label="Current Password"
                outlined
                type="password"
              ></v-text-field>
              <v-text-field
                v-model="formData.newPassword"
                label="New Password"
                outlined
                type="password"
              ></v-text-field>
              <v-text-field
                v-model="formData.confirmNewPassword"
                label="Confirm New Password"
                outlined
                type="password"
              ></v-text-field>

              <!-- Save Button -->
              <v-btn
                class="mt-4"
                :loading="loading"
                :disabled="!isModified || !isValid"
                color="primary"
                block
                @click="updateProfile"
              >
                Save Changes
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :timeout="snackbar.timeout" top>
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">Close</v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import api from "@/plugins/axios";
import defaultAvatar from '@/assets/default-avatar.png';
export default {
  name: "UserProfile",
  data() {
    return {
      formData: {
        username: "",
        email: "",
        image: "",
        imageFile: null,
        oldPassword: "",
        newPassword: "",
        confirmNewPassword: "",
        roles: [], // Roles as an array
      },
      defaultAvatar: defaultAvatar,
      originalData: {}, // For detecting changes
      loading: false,
      isValid: false,
      snackbar: {
        show: false,
        message: "",
        timeout: 3000,
      },
    };
  },
  computed: {
    isModified() {
      // Compare original data to detect changes
      return (
        this.formData.username !== this.originalData.username ||
        this.formData.email !== this.originalData.email ||
        this.formData.imageFile !== null ||
        this.formData.oldPassword !== "" ||
        this.formData.newPassword !== "" ||
        this.formData.confirmNewPassword !== ""
      );
    },
  },
  created() {
    this.fetchProfile();
  },
  methods: {
    async fetchProfile() {
  try {
    const response = await api.get("/api/users/me/");
    const baseURL = api.defaults.baseURL || "http://localhost:8000"; // Use Axios base URL
    this.formData.username = response.data.username;
    this.formData.email = response.data.email;
    this.formData.image = response.data.image
      ? `${baseURL}${response.data.image}`
      : this.defaultAvatar; // Handle default avatar
    this.formData.roles = response.data.roles; // Fetch roles as array
    this.originalData = { ...this.formData }; // Save original data
  } catch (error) {
    this.showSnackbar("Failed to load profile data.");
  }
},
    async updateProfile() {
      this.loading = true;

      // Validate password fields
      if (
        this.formData.newPassword &&
        this.formData.newPassword !== this.formData.confirmNewPassword
      ) {
        this.showSnackbar("New passwords do not match!");
        this.loading = false;
        return;
      }

      const formData = new FormData();
      formData.append("username", this.formData.username);
      formData.append("email", this.formData.email);
      if (this.formData.imageFile) {
        formData.append("image", this.formData.imageFile);
      }
      if (this.formData.oldPassword) {
        formData.append("old_password", this.formData.oldPassword);
      }
      if (this.formData.newPassword) {
        formData.append("new_password", this.formData.newPassword);
      }

      try {
        await api.patch("/api/users/me/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.showSnackbar("Profile updated successfully!");
        this.fetchProfile();
      } catch (error) {
        this.showSnackbar("Failed to update profile.");
      } finally {
        this.loading = false;
      }
    },
    showSnackbar(message) {
      this.snackbar.message = message;
      this.snackbar.show = true;
    },
    triggerImageUpload() {
      this.$refs.imageInput.$el.querySelector("input[type='file']").click(); // Properly trigger the file input
    },
    getRoleColor(role) {
      switch (role.toLowerCase()) {
        case "project_leader":
          return "blue lighten-4";
        case "administrator":
          return "red lighten-4";
        case "transcriber":
          return "green lighten-4";
        default:
          return "grey lighten-2";
      }
    },
    getRoleIcon(role) {
      switch (role.toLowerCase()) {
        case "project_leader":
          return "mdi-account-star";
        case "administrator":
          return "mdi-shield-account";
        case "transcriber":
          return "mdi-pencil";
        default:
          return "mdi-help-circle";
      }
    },
    getRoleText(role) {
      return role
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
  },
};
</script>

<style scoped>
.circle-image {
  object-fit: cover;
  border-radius: 50%;
  width: 128px;
  height: 128px;
}

.avatar-container {
  position: relative;
  cursor: pointer;
  display: inline-block;
  width: fit-content;
  margin: 0 auto;

}

.hover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 128px;
  height: 128px;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.avatar-container:hover .hover-overlay {
  opacity: 1;
}
.no-flex {
  flex: 0 0 auto;
}
</style>
